import { defineRecipe } from '@chakra-ui/react';
import { BorderWidths, Colors, Radii, Shadows, Space, FontSizes, FontWeights } from '../../types';

// Import Swiper styles, wrapped in `@layer swiper` so they lose to this recipe's
// `@layer recipes` rules.
import './carousel-swiper-vendor.css';

const buttonStyles = {
  color: Colors.TEXT_EMPHASIS,
  borderRadius: Radii.CIRCLE,
  width: Space.SPACE_12,
  height: Space.SPACE_12,
  borderWidth: BorderWidths.XS,
  borderColor: Colors.BORDER_DEFAULT,
  background: Colors.SURFACE_ELEVATED,
  boxShadow: Shadows.BASE,
};

const buttonStylesVertical = {
  color: Colors.TEXT_EMPHASIS,
  borderRadius: Radii.CIRCLE,
  width: Space.SPACE_8,
  minHeight: Space.SPACE_8,
  minWidth: Space.SPACE_4,
  height: Space.SPACE_8,
  borderWidth: BorderWidths.XS,
  borderColor: Colors.BORDER_DEFAULT,
  background: Colors.SURFACE_ELEVATED,
  boxShadow: Shadows.BASE,
  _hover: {
    background: Colors.TRANSPARENT,
    borderColor: Colors.BORDER_DEFAULT,
    boxShadow: Shadows.BASE,
  },
};

export const carouselRecipe = defineRecipe({
  base: {
    '& .swiper': {
      overflow: 'visible',
    },
    '& .swiper-wrapper': {},
    '& .swiper-button-prev': {
      zIndex: 'docked',
      pointerEvents: 'auto',
      ...buttonStyles,
      marginLeft: `-${Space.SPACE_8}`,
      '& svg': {
        width: '0.5em',
      },
      '& svg.fa-angle-left': {
        width: '0.75em',
        'vertical-align': '-.25em',
      },
    },
    '& .swiper-button-next': {
      ...buttonStyles,
      marginRight: `-${Space.SPACE_8}`,
      '& svg': {
        width: '0.5em',
      },
      '& svg.fa-angle-right': {
        width: '0.75em',
        'vertical-align': '-.25em',
      },
    },
    '& .swiper-slide': {
      overflow: 'hidden',
      p: 'px',
      '& .swiper-slide-shadow': {
        borderRadius: Radii.XL,
      },
    },
    '& .swiper-slide-next, & .swiper-slide-prev': {
      opacity: 0,
    },
    '& .swiper-cards': {
      '& .swiper-slide-next, & .swiper-slide-prev': {
        opacity: 1,
      },
    },
    // card effect next slide overlay
    '& .swiper-slide-shadow .swiper-slide-shadow-cards': {
      background: Colors.SURFACE_INVERT,
    },
    '& .swiper-button-disabled': {
      display: 'none',
    },
    '& .swiper-pagination-bullet': {
      background: Colors.ICON_DEFAULT,
      opacity: 1,
    },
    '& .swiper-pagination-bullets': {
      marginTop: Space.MD,
      bottom: `-${Space.SPACE_7}`,
      '& .swiper-pagination-bullet-active': {
        opacity: 1,
        background: Colors.ICON_ACTIVE,
      },
    },
    '& .swiper-pagination-progressbar.swiper-pagination-horizontal': {
      background: Colors.SURFACE_NEUTRAL_SUBDUED,
      borderRadius: Radii.FULL,
      overflow: 'hidden',
      marginBottom: Space.SM,
      top: `-${Space.SPACE_2}`,
      '& .swiper-pagination-progressbar-fill': {
        background: Colors.ICON_NEUTRAL,
      },
    },
    '& .swiper-pagination-fraction': {
      position: 'static',
      marginTop: Space.MD,
    },
    '& .swiper-horizontal.swiper-scrollbar.swiper-scrollbar.swiper-scrollbar-horizontal': {
      bottom: `-${Space.SPACE_8}`,
    },
    '& .swiper-container': {
      bg: Colors.BORDER_SUBDUED,
      h: '2',
      rounded: 'full',
    },
    '& .swiper-scrollbar': {
      bg: Colors.BORDER_MUTED,
      rounded: 'full',
      bottom: `-${Space.SPACE_6}`,
    },
  },

  variants: {
    variant: {
      centered: {
        '& .swiper': {
          overflow: 'visible',
        },
        '& .swiper-wrapper': {
          alignItems: 'center',
        },
        '& .swiper-button-prev': {
          marginLeft: `-${Space.SPACE_16}`,
        },
        '& .swiper-button-next': {
          marginRight: `-${Space.SPACE_16}`,
        },
        '& .swiper-slide-next, & .swiper-slide-prev': {
          opacity: 0.5,
        },
        '& .swiper-pagination-bullets': {
          marginTop: Space.MD,
          position: 'relative',
        },
        '& .swiper-slide': {
          marginRight: Space.SPACE_8,
        },
      },
      horizontal: {
        '& .swiper-button-prev': {
          position: 'absolute',
          zIndex: 'docked',
          pointerEvents: 'auto',
          ...buttonStyles,
          marginLeft: `-${Space.SPACE_8}`,
          _after: {
            fontSize: FontSizes.SM,
            fontWeight: FontWeights.BOLD,
            marginRight: Space.SPACE_1,
          },
        },
        '& .swiper-button-next': {
          ...buttonStyles,
          marginRight: `-${Space.SPACE_8}`,
          _after: {
            fontSize: FontSizes.SM,
            fontWeight: FontWeights.BOLD,
            marginLeft: Space.SPACE_1,
          },
        },
      },
      // Offset
      vertical: {
        '& .swiper': {
          overflow: 'hidden',
          marginTop: Space.XL,
          padding: Space.SPACE_4,
        },
        '& .swiper-wrapper': {},
        '& .swiper-button-next, & .swiper-button-prev': {
          ...buttonStylesVertical,
          zIndex: 'docked',
          left: '50%',
          transform: 'rotate(90deg)',
          transformOrigin: 'left center',
        },
        '& .swiper-button-prev': {
          top: `-${Space.SPACE_10}`,
          bottom: 'auto',
          marginLeft: '0px',
          position: 'absolute',
          transform: 'rotate(90deg)',
        },
        '& .swiper-button-next': {
          top: 'auto',
          bottom: `-${Space.SPACE_8}`,
          position: 'absolute',
          right: '0',
        },
        '& .swiper-vertical .swiper-pagination-bullets, & .swiper-pagination-vertical.swiper-pagination-bullets': {
          right: '0%',
          left: 'auto',
          top: '50%',
          transform: 'translate3d(0px, -50%, 0)',
        },
        '& .swiper-pagination-bullets': {
          marginTop: Space.XS,
          bottom: 'auto',
          '& .swiper-pagination-bullet-active': {
            opacity: 1,
            background: Colors.ICON_ACTIVE,
          },
        },
      },
      // Grouped: future sub-variant for verical
      // Split: future sub-variant for verical
    },

    // Opt-in via Core Carousel's `context` prop, keeping course-player fixes off other carousels. A separate
    // category, not a `variant` value: categories compose, so a course carousel keeps its `variant` styles.
    context: {
      course: {
        // Both axes must match: a 'visible' axis next to a 'hidden' one computes as 'auto', adding a scrollbar
        overflowX: 'hidden',
        overflowY: 'hidden',
        width: '100%',
        maxWidth: '100%',
        '& .swiper': {
          width: '100%',
          maxWidth: '100%',
        },
        '& .swiper-wrapper': {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          // stops slides stretching to match each other's height under autoHeight
          alignItems: 'flex-start',
        },
        '& .swiper-slide': {
          width: '100%',
          maxWidth: '100%',
          // flex items default to min-width:auto, blocking shrink below content size at narrow widths
          minWidth: 0,
          boxSizing: 'border-box',
          marginRight: 0,
        },
        '& .swiper-pagination-bullet': {
          display: 'inline-block',
          width: Space.SPACE_2,
          height: Space.SPACE_2,
          borderRadius: Radii.CIRCLE,
          cursor: 'pointer',
          // Restates Swiper's own horizontal bullet gap, since this rule outranks Swiper's layered bullet margin.
          margin: '0 var(--swiper-pagination-bullet-horizontal-gap, 4px)',
        },
      },
    },
  },

  // A compound entry, not `variants.context.course`: cva applies compoundVariants after the variant merge,
  // so these beat the `variant` category deterministically, where category-vs-category order is fragile.
  compoundVariants: [
    {
      context: 'course',
      css: {
        '& .swiper-slide-next, & .swiper-slide-prev': {
          // transform gives these a stacking context that can sit above the nav buttons even while
          // faded, so keep them inert rather than blocking clicks or showing the grabCursor
          opacity: 0.5,
          transform: 'scale(0.92)',
          pointerEvents: 'none',
          cursor: 'auto',
        },
        '& .swiper-button-prev': {
          position: 'absolute',
          top: '50%',
          left: Space.SPACE_2,
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 0,
          _hover: { bg: Colors.SURFACE_ELEVATED },
        },
        '& .swiper-button-next': {
          position: 'absolute',
          top: '50%',
          right: Space.SPACE_2,
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 0,
          zIndex: 'docked',
          pointerEvents: 'auto',
          _hover: { bg: Colors.SURFACE_ELEVATED },
        },
        '& .swiper-pagination-bullets': {
          position: 'absolute',
          left: 0,
          width: '100%',
          textAlign: 'center',
          zIndex: 'docked',
          bottom: `-${Space.SPACE_7}`,
          marginBottom: '24px',
        },
      },
    },
  ],
  // Nested selectors must stay `&`-prefixed: the cva()/css() engine `useRecipe` compiles through expands
  // only `&`-prefixed keys, so a bare `.swiper-*` key matches nothing.
} as any);
